import LinkButton from "../Buttons/LinkButton";

/**
 * CTABanner
 *
 * A full-width banner section with a background image on the right, a gradient
 * overlay fading left to right, and a text + button block on the left.
 * Used on consulting service pages as a mid-page or end-of-page call to action.
 *
 * Props:
 * - bgColor (string, required): Tailwind background color class used as a fallback
 *                               behind the image (e.g. "bg-orange-dark1").
 * - overlayFrom (string, required): Tailwind gradient classes for the left-to-right
 *                                   overlay (e.g. "from-orange-dark1 via-orange-dark1/80").
 * - imageSrc (string, required): URL of the background image displayed on the right.
 * - imageAlt (string, optional): Alt text for the background image. Defaults to "".
 * - imageClassName (string, optional): Additional Tailwind classes for image positioning.
 * - title (string | ReactNode, required): The bold heading text.
 * - description (string, required): Supporting paragraph text.
 * - buttonText (string, required): Label for the CTA button.
 * - buttonTo (string, required): Route path the button links to.
 * - buttonColour (string, optional): "blue" or "orange". Defaults to "blue".
 */

const CTABanner = ({
  bgColor,
  overlayFrom,
  imageSrc,
  imageAlt = "",
  imageClassName = "",
  title,
  description,
  buttonText,
  buttonTo,
  buttonColour = "blue",
  button2Text,
  button2To = "/contact-us",
  button2Colour = "blue",
}) => {
  return (
    <section className={`relative w-full overflow-hidden ${bgColor} my-16 lg:my-24 xl:my-[160px]`}>
      {/* Background image */}
      <img
        src={imageSrc}
        alt={imageAlt}
        className={`absolute inset-0 w-full h-full object-cover ${imageClassName}`}
        loading="lazy"
      />

      {/* Gradient overlay */}
      <div className={`absolute inset-0 bg-gradient-to-r ${overlayFrom} to-transparent z-[1]`} />

      {/* Text content */}
      <div className="relative z-[2] px-8 md:px-16 py-12 md:py-20 lg:py-28 md:w-2/3 lg:w-1/2 text-center md:text-left">
        <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-7xl font-bold text-white mb-6 [text-shadow:1px_1px_1px_black]">
          {title}
        </h2>
        <p className="!text-sm md:!text-base xl:!text-2xl text-white mb-8 max-w-md xl:max-w-2xl mx-auto md:mx-0 [text-shadow:1px_1px_1px_black]">
          {description}
        </p>
        <div className="flex flex-col gap-3">
          <LinkButton to={buttonTo} colour={buttonColour} className="text-sm md:text-lg xl:text-2xl md:px-4 md:py-2 xl:px-8 xl:py-4">
            {buttonText}
          </LinkButton>
          {button2Text && (
            <LinkButton to={button2To} colour={button2Colour} className="text-sm md:text-lg xl:text-2xl md:px-4 md:py-2 xl:px-8 xl:py-4">
              {button2Text}
            </LinkButton>
          )}
        </div>
      </div>
    </section>
  );
};

export default CTABanner;
